import { useEffect, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import classNames from 'classnames';
import type {
  DragSourceMonitor,
  DropTargetMonitor,
} from 'react-dnd/dist/types';

import DraggableTypes from '../../constants/draggable_types';
import type { FCC } from '../../types/common';
import {
  DRAG_DIRECTION_AFTER,
  DRAG_DIRECTION_BEFORE,
  type DragDirection,
} from './constants';

import styles from './sortable_list.module.css';

type DragDropData = {
  id: string;
  index: number;
  type?: string;
  title?: string;
  iconClass?: string;
  direction?: DragDirection;
};

type DragDropConnectProps = {
  className?: string;
  id: string;
  index: number;
  type?: string;
  title?: string;
  iconClass?: string;
  direction?: DragDirection;
  noPreview?: boolean;
  dropSuccess?: (dragData: DragDropData, dropData: DragDropData) => void;
  moveElement: (
    dragIndex: number,
    hoverIndex: number,
    direction: DragDirection,
  ) => void;
  beginDrag?: (dragData: DragDropData) => void;
  endDrag?: (props: DragDropConnectProps) => void;
  canDrag?: (props: DragDropConnectProps) => boolean;
};

const DragSourceDropTarget: FCC<DragDropConnectProps> = (props) => {
  // here we need to discard non html props
  const {
    children,
    className,
    dropSuccess,
    iconClass,
    id,
    index,
    direction,
    title,
    moveElement,
    beginDrag,
    endDrag,
    canDrag,
    noPreview,
    ...htmlProps
  } = props;

  const ref = useRef<HTMLDivElement>(null);

  const [collectedDragProps, drag, preview] = useDrag({
    type: DraggableTypes.BOX,
    item: {
      id: props.id,
      index: props.index,
      type: props.type,
      title: props.title,
      iconClass: props.iconClass,
      direction: props.direction,
    } as DragDropData,
    collect: (monitor: DragSourceMonitor<DragDropData, DragDropData>) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag() {
      return props.canDrag ? props.canDrag(props) : true;
    },
    end(item, monitor) {
      const dropResult = monitor.getDropResult();
      if (props.endDrag) {
        props.endDrag(props);
      }
      if (dropResult && props.dropSuccess) {
        const dropData = {
          id: dropResult.id,
          type: dropResult.type,
          index: dropResult.index,
        };
        const dragData = { id: item.id, type: item.type, index: item.index };
        props.dropSuccess(dragData, dropData);
      }
    },
  });

  const [collectedDropProps, drop] = useDrop({
    accept: DraggableTypes.BOX,
    collect: (monitor: DropTargetMonitor<DragDropData, DragDropData>) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    hover(item, monitor) {
      if (!props.moveElement) {
        return;
      }
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = props.index;

      let dragDirection: DragDirection = DRAG_DIRECTION_BEFORE;

      // Determine rectangle on screen
      const { bottom, top } = ref.current.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY = (bottom - top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      if (clientOffset) {
        // Get pixels to the top
        const hoverClientY = clientOffset.y - top;

        if (hoverClientY < hoverMiddleY) {
          dragDirection = DRAG_DIRECTION_BEFORE;
        }
        if (hoverClientY > hoverMiddleY) {
          dragDirection = DRAG_DIRECTION_AFTER;
        }
      }

      props.moveElement(dragIndex, hoverIndex, dragDirection);
    },
  });

  useEffect(() => {
    if (props.noPreview) {
      preview(getEmptyImage(), { captureDraggingState: true });
    }
  }, [preview, props.noPreview]);

  const isActive = collectedDropProps.canDrop && collectedDropProps.isOver;

  drag(drop(ref));

  const directionClassName = direction
    ? {
        [styles[`drag_${direction}`]]: isActive,
      }
    : undefined;

  return (
    <div
      ref={ref}
      {...htmlProps}
      className={classNames(className, directionClassName, {
        [styles.candrop]: isActive,
        [styles.dragging]: collectedDragProps.isDragging,
      })}
    >
      {children}
    </div>
  );
};

export default DragSourceDropTarget;
